"use client";
import React, { useEffect, useState } from "react";
import type { StaticImageData } from "next/image";
import IndustryHeader from "./IndustryHeader";
import { Images } from "@/app/assets/shared";
import Image from "next/image";

interface FeatureItem {
  name: string;
  content: string;
}

interface TabItem {
  key: string;
  label: string;
  heading: string;
  description: string;
  items: FeatureItem[];
  icon: any;
  iconGray: any;
}

interface tabImageSlider {
  tabName: string | undefined;
  tabImageArray: StaticImageData[] | string[];
}

interface AdvanceFeaturesProps {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  featuresArray: TabItem[];
  sliderArray: tabImageSlider[];
  button: React.ReactNode;
}

export default function Advancedfeature({
  title,
  subtitle,
  description,
  featuresArray,
  sliderArray = [],
  button,
}: AdvanceFeaturesProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(260);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const slideGap = 24;

  const trackTranslate = -(slideWidth + slideGap) * slideIndex;

  const currentTab = featuresArray[activeTab];
  const features = currentTab?.items || [];

  const isAdminTab = currentTab?.key.toLowerCase().includes("admin");

  const slides =
    sliderArray.find((item) => item.tabName === currentTab?.key)
      ?.tabImageArray ?? [];

  const midIndex = Math.ceil(features.length / 2);
  const featuresLeft = features.slice(0, midIndex);
  const featuresRight = features.slice(midIndex);

  useEffect(() => {
    const updateWidth = () => {
      if (typeof window !== "undefined") {
        setSlideWidth(
          window.innerWidth >= 1024
            ? 320
            : window.innerWidth >= 1024
              ? 280
              : 260,
        );
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    setSlideIndex(0);
  }, [activeTab]);

  return (
    <section
      id="advanced-feature"
      className="advanced-feature pb-[100px] bg-[#ECF0F6] relative  overflow-hidden"
    >
      <Image
        src={Images?.AdvancedBg}
        alt="texture"
        width={200}
        height={200}
        className="absolute bottom-0 left-0 "
      />

      <IndustryHeader
        title={title}
        subtitle={subtitle}
        description={description}
        button={button}
      />

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] bg-transparent">
        {/* Desktop Tabs */}
        <div className="hidden lg:flex flex-wrap items-center border-b border-[#E6EEF8]">
          {featuresArray?.map((tab, idx) => {
            const isActive = idx === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(idx)}
                aria-label={`Switch to ${tab.label} tab`}
                className={`group relative uppercase tracking-wide text-[13px] md:text-[14px] font-medium flex items-center gap-2 px-[40px] py-3 ${
                  isActive
                    ? "text-[#225BD4] bg-white rounded-t-lg"
                    : "text-[#6B7280]"
                }`}
              >
                <Image
                  src={isActive ? tab.icon : tab.iconGray}
                  alt={`${tab.label} icon`}
                  width={18}
                  height={18}
                  className="w-[18px] h-[18px]"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                {tab.label}
                <span
                  className={`absolute -bottom-[2px] left-0 h-[2px] transition-all duration-300 ${
                    isActive
                      ? "w-full bg-[#225BD4]"
                      : "w-0 bg-transparent group-hover:w-full group-hover:bg-[#9BB8F3]"
                  }`}
                />
              </button>
            );
          })}
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden relative mb-6">
          <button
            type="button"
            aria-label={`${isDropdownOpen ? "Close" : "Open"} feature tabs dropdown`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full bg-white rounded-lg shadow-[0_4px_12px_rgba(6,24,44,0.08)] ring-1 ring-[#E8EEF6] px-4 py-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Image
                src={currentTab.icon}
                alt={`${currentTab.label} icon`}
                width={20}
                height={20}
                className="w-[20px] h-[20px]"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <span className="text-[#0B1320] font-medium text-[15px]">
                {currentTab.label}
              </span>
            </div>
            <svg
              className={`w-5 h-5 text-[#6B7280] transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-[0_8px_24px_rgba(6,24,44,0.12)] ring-1 ring-[#E8EEF6] overflow-hidden z-50">
              {featuresArray?.map((tab, idx) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(idx);
                    setIsDropdownOpen(false);
                  }}
                  aria-label={`Select ${tab.label}`}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                    idx === activeTab
                      ? "bg-[#F0F5FF] text-[#225BD4]"
                      : "text-[#6B7280] hover:bg-[#F9FAFB]"
                  }`}
                >
                  <Image
                    src={idx === activeTab ? tab.icon : tab.iconGray}
                    alt={`${tab.label} icon`}
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                  <span className="font-medium text-[14px]">{tab.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative z-3 bg-white rounded-[20px] lg:rounded-tl-none shadow-[0_10px_40px_rgba(6,24,44,0.08)] ring-1 ring-[#E8EEF6] px-6 md:px-10 lg:px-12 py-6 md:py-8">
          <div className="w-full pt-6 md:pt-8 flex flex-col gap-2">
            <h3 className="clash-font font-[500] text-[30px] md:text-[38px] text-[#0B1320]">
              {currentTab.heading}
            </h3>
            <p className="text-[#636C76] text-[15px] md:text-[17px] leading-[1.8] max-w-[980px]">
              {currentTab.description}
            </p>
          </div>

          {/* Mobile Layout */}
          <div className="w-full py-10 flex flex-col lg:hidden gap-12">
            <div className="relative flex items-center justify-center">
              <div
                className={`relative overflow-hidden transition-all duration-300 ${isAdminTab ? "w-full h-auto" : "w-[260px] h-[560px]"}`}
              >
                <div
                  className="flex items-center"
                  style={{
                    gap: `${slideGap}px`,
                    transform: `translateX(${trackTranslate}px)`,
                    transition: "transform 400ms ease",
                  }}
                >
                  {slides?.map((src, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 flex justify-center"
                      style={{ width: `${slideWidth}px` }}
                    >
                      <Image
                        src={src}
                        alt={`slide-${i + 1}`}
                        width={isAdminTab ? 1000 : slideWidth}
                        height={isAdminTab ? 600 : Math.round(slideWidth * 2)}
                        className="select-none h-auto rounded-[12px] w-full"
                        draggable={false}
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {slides.length > 1 && (
                <div className="absolute bottom-[-45px] left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {slides?.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSlideIndex(i)}
                      className={`relative grid place-items-center box-border rounded-full transition-all duration-200 cursor-pointer ${
                        i === slideIndex
                          ? "w-[17px] h-[17px] bg-white border-2 border-[#225BD4]"
                          : "w-[12px] h-[12px] bg-[#D9E2ED]"
                      }`}
                      aria-label={`Go to slide ${i + 1}`}
                    >
                      {i === slideIndex && (
                        <span className="w-[9px] h-[9px] rounded-full bg-[#225BD4]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-8 mt-6">
              {features?.map((item, idx) => {
                const isActive = idx === slideIndex;
                return (
                  <div
                    key={item.name}
                    onClick={() => setSlideIndex(idx)}
                    className={`transition-all duration-300 cursor-pointer ${isActive ? "opacity-100" : "opacity-40"}`}
                  >
                    <div
                      className={`p-4  transition-all ${isActive ? "" : "ring-0"}`}
                    >
                      <h4 className="clash-font font-[500] text-[22px] text-[#0B1320]">
                        {item.name}
                      </h4>
                      <p className="text-[#636C76] leading-[1.8] text-[15px]">
                        {item.content}
                      </p>
                    </div>
                    <div className="h-px w-full bg-[#E6EEF8] mt-3" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:block w-full py-10 pt-20">
            {isAdminTab ? (
              <div className="grid grid-cols-12 gap-12 items-center">
                <div className="col-span-4 flex flex-col gap-10 max-h-[589px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {features?.map((item, idx) => {
                    const isActive = idx === slideIndex;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSlideIndex(idx)}
                        className={`transition-all duration-300 cursor-pointer ${isActive ? "opacity-100" : "opacity-40"}`}
                      >
                        <div
                          className={`p-4 transition-all ${isActive ? "" : "ring-0"}`}
                        >
                          <h4 className="clash-font font-[500] text-[30px] text-[#0B1320]">
                            {item.name}
                          </h4>
                          <p className="text-[#636C76] leading-[1.8] text-[18px]">
                            {item.content}
                          </p>
                        </div>
                        <div className="h-px w-full bg-[#E6EEF8] mt-3" />
                      </div>
                    );
                  })}
                </div>

                <div className="col-span-8 relative flex flex-col items-center">
                  <div className="w-full overflow-hidden">
                    <div
                      className="flex transition-transform duration-400 ease"
                      style={{ transform: `translateX(${-100 * slideIndex}%)` }}
                    >
                      {slides?.map((src, i) => (
                        <div key={i} className="min-w-full flex justify-end">
                          <Image
                            src={src}
                            alt="Laptop View"
                            width={1200}
                            height={800}
                            className="w-full h-auto object-contain drop-shadow-2xl"
                            sizes="(max-width: 1024px) 100vw, 50vw"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {slides.length > 1 && (
                    <div className="mt-8 flex items-center gap-2">
                      {slides?.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setSlideIndex(i)}
                          aria-label={`Go to slide ${i + 1}`}
                          className={`relative grid place-items-center box-border rounded-full transition-all duration-200 cursor-pointer ${
                            i === slideIndex
                              ? "w-[17px] h-[17px] bg-white border-2 border-[#225BD4]"
                              : "w-[12px] h-[12px] bg-[#D9E2ED]"
                          }`}
                        >
                          {i === slideIndex && (
                            <span className="w-[9px] h-[9px] rounded-full bg-[#225BD4]" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-12 items-start">
                <div className="flex flex-col gap-10">
                  {featuresLeft?.map((item, idx) => {
                    const isActive = idx === slideIndex;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSlideIndex(idx)}
                        className={`transition-all duration-300 cursor-pointer ${isActive ? "opacity-100" : "opacity-40"}`}
                      >
                        <div
                          className={`p-5 transition-all ${isActive ? "" : "ring-0"}`}
                        >
                          <h4 className="clash-font font-[500] text-[30px] text-[#0B1320]">
                            {item.name}
                          </h4>
                          <p className="text-[#636C76] leading-[1.8] text-[18px]">
                            {item.content}
                          </p>
                        </div>
                        <div className="h-px w-full bg-[#E6EEF8] mt-3" />
                      </div>
                    );
                  })}
                </div>

                <div className="relative flex items-center justify-center">
                  <div className="relative w-[320px] h-[662px] flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                      <div
                        className="flex items-center"
                        style={{
                          gap: `${slideGap}px`,
                          transform: `translateX(${trackTranslate}px)`,
                          transition: "transform 400ms ease",
                        }}
                      >
                        {slides?.map((src, i) => (
                          <Image
                            key={i}
                            src={src}
                            alt={`slide-${i + 1}`}
                            width={slideWidth}
                            height={Math.round(slideWidth * 2)}
                            className="select-none h-auto rounded-[24px]"
                            sizes="(max-width: 1024px) 100vw, 50vw"
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-[-45px] left-1/2 -translate-x-1/2 flex items-center gap-2">
                    {slides?.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSlideIndex(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`relative grid place-items-center box-border rounded-full transition-all duration-200 cursor-pointer ${
                          i === slideIndex
                            ? "w-[17px] h-[17px] bg-white border-2 border-[#225BD4]"
                            : "w-[12px] h-[12px] bg-[#D9E2ED]"
                        }`}
                      >
                        {i === slideIndex && (
                          <span className="w-[9px] h-[9px] rounded-full bg-[#225BD4]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-10">
                  {featuresRight?.map((item, idx) => {
                    const actualIdx = idx + featuresLeft.length;
                    const isActive = actualIdx === slideIndex;
                    return (
                      <div
                        key={item.name}
                        onClick={() => setSlideIndex(actualIdx)}
                        className={`transition-all duration-300 cursor-pointer ${isActive ? "opacity-100" : "opacity-40"}`}
                      >
                        <div
                          className={`p-5 transition-all ${isActive ? "" : "ring-0"}`}
                        >
                          <h4 className="clash-font font-[500] text-[30px] text-[#0B1320]">
                            {item.name}
                          </h4>
                          <p className="text-[#636C76] leading-[1.8] text-[18px]">
                            {item.content}
                          </p>
                        </div>
                        <div className="h-px w-full bg-[#E6EEF8] mt-3" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
